import api from "./api.service";
import { adminTokenService } from "./admin.token.service";

export interface AdminDispatcher {
    id: number;
    code?: string;
    firstName: string;
    lastName: string;
    isO2Country?: string; // Sometimes returned as isO2Country or ISO2Country depending on serialization
    iso2Country?: string;
    kindOfDNI: string;
    dni: string;
    status: number; // 0: Inactive, 1: Active, 2: Deleted
    phoneNumber?: string;
    email?: string;
    affiliationDate?: string;
    state?: string;
    zipCode?: string;
}

export interface GetAdminDispatchersParams {
    page?: number;
    pageSize?: number;
    status?: number;
    SearchString?: string;
}

export interface GetAdminDispatchersResponse {
    statusCode: number;
    message: string;
    data: AdminDispatcher[];
    pagination: {
        totalCount: number;
        page: number;
        pageSize: number;
        totalPages: number;
    };
}

export interface CreateDispatcherData {
    firstName: string;
    lastName: string;
    iso2Country: string;
    kindOfDNI: string;
    dni: string;
    status: number;
    phoneNumber: string;
    email: string;
    state: string;
    zipCode: string;
}

export interface UpdateDispatcherData {
    id: number;
    firstName: string;
    lastName: string;
    iso2Country: string;
    kindOfDNI: string;
    dni: string;
    status: number;
    phoneNumber: string;
    email: string;
    state: string;
    zipCode: string;
}

export interface UpdateDispatcherStatusData {
    id: number;
    status: number;
}

export interface DeleteDispatcherData {
    id: number;
}

class AdminDispatcherService {
    private getHeaders() {
        const token = adminTokenService.getAdminToken();
        return {
            Authorization: `${token}`,
        };
    }

    async getDispatchers(params?: GetAdminDispatchersParams): Promise<GetAdminDispatchersResponse> {
        try {
            const response = await api.get<GetAdminDispatchersResponse>(
                "/admin/get-dispatchers",
                {
                    params,
                    headers: this.getHeaders(),
                }
            );
            return response.data;
        } catch (error) {
            console.error("Error fetching admin dispatchers:", error);
            throw error;
        }
    }

    async getDispatcherById(id: number): Promise<{ statusCode: number; message: string; data: AdminDispatcher }> {
        try {
            const response = await api.get<{ statusCode: number; message: string; data: AdminDispatcher }>(
                `/admin/get-dispatcher/${id}`,
                {
                    headers: this.getHeaders(),
                }
            );
            return response.data;
        } catch (error) {
            console.error(`Error fetching admin dispatcher ${id}:`, error);
            throw error;
        }
    }

    async createDispatcher(data: CreateDispatcherData): Promise<any> {
        try {
            const response = await api.post(
                "/admin/create-dispatcher",
                data,
                {
                    headers: this.getHeaders(),
                }
            );
            return response.data;
        } catch (error) {
            console.error("Error creating admin dispatcher:", error);
            throw error;
        }
    }

    async updateDispatcher(data: UpdateDispatcherData): Promise<any> {
        try {
            const response = await api.post(
                "/admin/update-dispatcher",
                data,
                {
                    headers: this.getHeaders(),
                }
            );
            return response.data;
        } catch (error) {
            console.error("Error updating admin dispatcher:", error);
            throw error;
        }
    }

    async changeDispatcherStatus(data: UpdateDispatcherStatusData): Promise<any> {
        try {
            const response = await api.post(
                "/admin/change-dispatcher-status",
                data,
                {
                    headers: this.getHeaders(),
                }
            );
            return response.data;
        } catch (error) {
            console.error("Error changing admin dispatcher status:", error);
            throw error;
        }
    }

    async deleteDispatcher(data: DeleteDispatcherData): Promise<any> {
        try {
            const response = await api.post(
                "/admin/delete-dispatcher",
                data,
                {
                    headers: this.getHeaders(),
                }
            );
            return response.data;
        } catch (error) {
            console.error("Error deleting admin dispatcher:", error);
            throw error;
        }
    }
}

export const adminDispatcherService = new AdminDispatcherService();
